import {
  ProductsListingSkuQuery,
  useProductsListingSkuQuery,
} from '@/modules/product/gql/__generated/products.generated';
import { ProductVariant } from '@/modules/product/models/product-variant.interface';
import { productApiToClient } from '@/modules/product/services/productTransform';
import {
  fetchAllProductsBySkusPages,
  getProductsBySkusPageLimit,
} from '@/modules/product/services/productsBySkusPagination';

import { ProductPdpInterface } from '../models/product-pdp.interface';
import { getMatchingSku } from '../utils';

const EMPTY_PRODUCTS_LISTING_QUERY_DATA: ProductsListingSkuQuery = {
  products: {
    total: 0,
    results: [],
  },
};

const fetchAllProductsListingBySku = (skus: string[]) =>
  fetchAllProductsBySkusPages(skus, EMPTY_PRODUCTS_LISTING_QUERY_DATA, (variables) =>
    useProductsListingSkuQuery.fetcher(variables)()
  );

// Helper function to find variants by SKUs and check their status
export const getVariantsStatus = (
  products: (ProductPdpInterface | null)[],
  skus: string[],
  hasLoaded: boolean
) => {
  // If products have not loaded yet (or the full query failed), return default values.
  if (!hasLoaded) {
    return {
      skusVariant: [],
      hasUnpublishedItems: false,
      hasOutOfStockItems: false,
      hasUnresolvedItems: false,
      validProducts: [],
    };
  }

  const skusVariant = skus.map((sku) => {
    const product = products.find(
      (product) =>
        product?.variants?.find((variant: ProductVariant) => variant?.sku === sku) ||
        product?.masterVariant?.sku === sku
    );

    const variants = [product?.masterVariant, ...(product?.variants || [])];

    return variants.find((variant) => variant?.sku === sku);
  });

  // Check if we have valid variants before determining status
  const hasAnyValidVariant = skusVariant.some(
    (variant) => variant !== undefined && variant !== null
  );

  // Fail closed: once products have loaded, a cart SKU that does not resolve to a variant
  // (deleted SKU, master/variant mismatch) is treated as unavailable so it cannot slip past
  // the checkout stock gate. Paged fetching prevents query-limit truncation from looking
  // like an unresolved item.
  const hasUnresolvedItems =
    skus.length > 0 && skusVariant.some((variant) => variant === null || variant === undefined);

  // Only check status if we have valid variants, otherwise default to false
  const hasUnpublishedItems =
    hasAnyValidVariant ?
      skusVariant.some((variant) => variant !== null && variant !== undefined && !variant.published)
    : false;

  const hasOutOfStockItems =
    hasUnresolvedItems ||
    (hasAnyValidVariant ?
      skusVariant.some((variant) => variant !== null && variant !== undefined && !variant.isOnStock)
    : false);

  // Filter for valid products (published)
  const validProducts = products.filter((product): product is ProductPdpInterface => {
    if (!product) {
      return false;
    }

    const productSku = skus.find((sku) => {
      const variants = [product.masterVariant, ...(product.variants || [])];
      return variants.some((variant) => variant?.sku === sku);
    });

    if (!productSku) {
      return false;
    }

    const variants = [product.masterVariant, ...(product.variants || [])];
    const variant = variants.find((v) => v?.sku === productSku);

    return Boolean(variant?.published);
  });

  return {
    skusVariant,
    hasUnpublishedItems,
    hasOutOfStockItems,
    hasUnresolvedItems,
    validProducts,
  };
};

export function useProductsBySkus(skus: string[] = [], enabled: boolean = true) {
  const queryVariables = {
    skus,
    limit: getProductsBySkusPageLimit(skus.length),
    offset: 0,
  };
  const {
    data,
    isPending,
    isSuccess,
    refetch: refetchProducts,
    ...rest
  } = useProductsListingSkuQuery(queryVariables, {
    enabled: enabled && skus.length > 0,
    queryFn: () => fetchAllProductsListingBySku(skus),
  });

  const products =
    data?.products.results.map((product) =>
      productApiToClient({
        product: { product },
        sku: getMatchingSku(product, skus),
      })
    ) || [];
  const { hasUnpublishedItems, hasOutOfStockItems, hasUnresolvedItems, validProducts } =
    getVariantsStatus(products, skus, isSuccess);

  const refetch = async () => {
    const { data: newData, ...result } = await refetchProducts();
    const products =
      newData?.products.results.map((product) =>
        productApiToClient({
          product: { product },
          sku: getMatchingSku(product, skus),
        })
      ) || [];
    const {
      hasUnpublishedItems: hasUnpublishedItemsNew,
      hasOutOfStockItems: hasOutOfStockItemsNew,
      hasUnresolvedItems: hasUnresolvedItemsNew,
      validProducts: validProductsNew,
    } = getVariantsStatus(products, skus, result.isSuccess);

    return {
      products,
      data: newData,
      hasUnpublishedItems: hasUnpublishedItemsNew,
      hasOutOfStockItems: hasOutOfStockItemsNew,
      hasUnresolvedItems: hasUnresolvedItemsNew,
      validProducts: validProductsNew,
      ...result,
    };
  };

  return {
    products,
    data,
    hasUnpublishedItems,
    hasOutOfStockItems,
    hasUnresolvedItems,
    validProducts,
    isPending,
    refetch,
    ...rest,
  };
}
